import React from 'react'
import {
  LayoutDashboard,
  Building,
  FileStack,
  Wallet,
  Users,
  ScrollText,
  Settings,
  LogOut,
  X,
} from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import BritLogo from '../assets/white-brit-logo.svg'
import { useUserProfile } from '../context/UserProfileContext'
import { AuthService } from '../utils/authService'

type NavItem = {
  label: string
  icon: React.ComponentType<{ className?: string }>
  href: string
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, href: '/admin/dashboard' },
  { label: 'Company Management', icon: Building, href: '/admin/companies' },
  // { label: 'Admin Management', icon: Shield, href: '/admin/admins' },
  { label: 'Filling Management', icon: FileStack, href: '/admin/filings' },
  { label: 'Finance Balance', icon: Wallet, href: '/admin/finance' },
  { label: 'Users Management', icon: Users, href: '/admin/users' },
  { label: 'Review Request', icon: ScrollText, href: '/admin/reports' },
  { label: 'Settings', icon: Settings, href: '/admin/settings' },
]

type Props = {
  onClose?: () => void
}

export default function AdminSidebar({ onClose }: Props) {
  const location = useLocation()
  const { user, getUserDisplayName } = useUserProfile()

  const displayName = getUserDisplayName()
  const email = user?.email || '—'

  const handleLogout = () => {
    AuthService.logout()
  }

  const getInitials = () => {
    const name = `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
    if (name) {
      const parts = name.split(' ').filter(Boolean)
      return parts.slice(0, 2).map(n => n.charAt(0).toUpperCase()).join('') || 'U'
    }
    return email.charAt(0).toUpperCase() || 'U'
  }

  return (
    <aside className='relative flex h-screen w-[260px] max-w-[85vw] flex-col bg-[#0f0b10] text-white overflow-hidden'>
      <div
        className='pointer-events-none absolute inset-0 opacity-70'
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.05) 0, transparent 35%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.04) 0, transparent 35%)',
        }}
      />
      <div className='relative flex flex-col h-full px-5 py-6'>
        <div className='flex items-center justify-between gap-3 mb-8 lg:mb-10'>
          <div className='flex items-center gap-3'>
            <img src={BritLogo} alt='BritFiler' className='h-10 w-auto' />
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className='lg:hidden p-1.5 rounded-md text-gray-200 hover:bg-white/10'
              aria-label='Close menu'
            >
              <X className='h-5 w-5' />
            </button>
          )}
        </div>

        <p className='relative mb-4 text-xs font-semibold uppercase tracking-wide text-gray-400'>
          Main Menu
        </p>

        <nav className='relative flex-1 space-y-1 overflow-y-auto pr-1 -mr-1'>
          {navItems.map(item => {
            const Icon = item.icon
            const active = location.pathname.startsWith(item.href)
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onClose}
                className={`flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold transition ${
                  active
                    ? 'bg-[#ff4f3c] text-white shadow-lg'
                    : 'text-gray-200 hover:bg-white/10'
                }`}
              >
                <Icon className='h-4 w-4 flex-shrink-0' />
                <span className='truncate'>{item.label}</span>
              </Link>
            )
          })}
        </nav>

        <div className='relative mt-auto pt-4'>
          <div className='mb-4 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-xs text-gray-200 flex items-center gap-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-sm font-semibold flex-shrink-0'>
              {getInitials()}
            </div>
            <div className='flex-1 min-w-0'>
              <p className='font-semibold text-white truncate'>{displayName}</p>
              <p className='text-gray-300 truncate'>{email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className='flex w-full items-center gap-2 rounded-xl bg-white/10 px-3 py-3 text-sm font-semibold text-white hover:bg-white/20 transition'
          >
            <LogOut className='h-4 w-4' />
            Logout
          </button>
        </div>
      </div>
    </aside>
  )
}
